'use client';

import React, { useMemo } from 'react';
import type { PublicArticle } from '@/lib/types';
import { ArticleCard } from '@/components/ArticleCard';
import { useBrowseFilters } from '@/lib/hooks/useBrowseFilters';
import { cn } from '@/lib/utils';

/**
 * ResultsList — filtered, sorted stack of article rows.
 *
 * Runs entirely client-side over the fully-loaded articles array; this keeps
 * filter interactions instant. For a larger catalog we'd move to server
 * filtering with `router.replace` triggering a refetch.
 */

export interface ResultsListProps {
  articles: PublicArticle[];
  onCountChange?: (count: number) => void;
}

export function ResultsList({ articles, onCountChange }: ResultsListProps) {
  const { filters, clear } = useBrowseFilters();

  const filtered = useMemo(() => {
    const q = filters.q.toLowerCase().trim();
    return articles
      .filter((a) => {
        if (filters.goal && !a.health_goals.includes(filters.goal)) return false;
        if (filters.tier && a.evidence_tier !== filters.tier) return false;
        if (filters.kind && a.kind !== filters.kind) return false;
        if (filters.topic && !a.topics.includes(filters.topic)) return false;
        if (filters.supplement && a.supplement !== filters.supplement) return false;
        if (filters.time) {
          const m = a.reading_time_minutes;
          // Buckets partition the range cleanly so a 15-min article belongs
          // to exactly one: [<5) | [5,15) | [15,+).
          if (filters.time === '<5' && m >= 5) return false;
          if (filters.time === '5-15' && (m < 5 || m >= 15)) return false;
          if (filters.time === '15+' && m < 15) return false;
        }
        if (q) {
          const hay = [
            a.title,
            a.subtitle ?? a.dek ?? '',
            a.executive_summary,
            a.supplement,
            ...a.health_goals,
            ...a.topics,
          ]
            .join(' ')
            .toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        switch (filters.sort) {
          case 'oldest':
            return dateStr(a) - dateStr(b);
          case 'sources':
            return (b.sources_count ?? 0) - (a.sources_count ?? 0);
          case 'shortest':
            return (a.reading_time_minutes ?? 0) - (b.reading_time_minutes ?? 0);
          case 'newest':
          default:
            return dateStr(b) - dateStr(a);
        }
      });
  }, [articles, filters]);

  React.useEffect(() => {
    onCountChange?.(filtered.length);
  }, [filtered.length, onCountChange]);

  if (filtered.length === 0) {
    return (
      <div
        className={cn(
          'max-w-container mx-auto px-10 mt-8',
          'text-center py-20 bg-white border border-border rounded-xl'
        )}
      >
        <h3
          className="font-display font-medium text-[28px] leading-tight text-warm-navy m-0 mb-3"
          style={{ fontVariationSettings: '"opsz" 36' }}
        >
          No studies match these filters
        </h3>
        <p className="font-sans text-[15px] text-slate m-0 mb-5">
          Try widening the evidence tier or clearing goal constraints.
        </p>
        <button
          onClick={clear}
          className={cn(
            'inline-flex items-center gap-2 px-5 py-[11px] rounded-md',
            'bg-warm-navy text-white font-ui text-[13px] font-medium',
            'hover:bg-aviado-blue transition-colors duration-fast'
          )}
        >
          Clear filters
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-container mx-auto px-10 mt-6">
      {filtered.map((article) => (
        <ArticleCard key={article.id} article={article} variant="result-row" />
      ))}
    </div>
  );
}

function dateStr(a: PublicArticle): number {
  const s = a.published_at ?? a.created_at;
  const t = s ? Date.parse(s) : NaN;
  return Number.isFinite(t) ? t : 0;
}

export default ResultsList;
